/**
 * useAnonPage — a closed house's visitor page, answering the wall's search field. The server renders
 * the page for the URL's `?q=` (`initial`); a later keystroke asks `GET /wall?q=` for the veiled
 * matches, or for the composition again when the field is emptied, and the page swaps once that
 * answer lands. Takes the query the field has settled on (`useSettledQuery`) and is stale-guarded as `useWallPages`
 * is — a slow answer for an abandoned query never lands over a newer one — and the tiles on screen stay until the
 * new page arrives.
 *
 * The page is one finite response (`AnonWallResponse`): no cursor, nothing to page into. A failed
 * fetch leaves the wall as it was.
 */
import {useEffect, useRef, useState} from 'react'
import type {AnonWallResponse} from '@core/wall/contract'

/** The page on screen and the query it answers — the server-rendered page answers the URL's `?q=`. */
export type AnonPage = {page: AnonWallResponse; query: string}

export function useAnonPage(q: string, initial: AnonWallResponse | undefined): AnonPage | undefined {
    const [answer, setAnswer] = useState<AnonPage | undefined>(() => initial && {page: initial, query: q})
    const initialQuery = useRef(q)
    const skipFirst = useRef(true)
    const seq = useRef(0)

    useEffect(() => {
        if (!initial) return
        if (skipFirst.current) {
            skipFirst.current = false
            if (q === initialQuery.current) return
        }
        const mine = ++seq.current
        const controller = new AbortController()
        void (async () => {
            try {
                const res = await fetch(`/wall${q ? `?${new URLSearchParams({q})}` : ''}`, {signal: controller.signal})
                if (!res.ok) return
                const body = (await res.json()) as AnonWallResponse
                if (seq.current === mine) setAnswer({page: body, query: q})
            } catch (err) {
                if ((err as Error)?.name !== 'AbortError') console.error('[useAnonPage] GET /wall failed', err)
            }
        })()
        return () => controller.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `initial` only gates the hook on; `q` is the trigger
    }, [q])

    return answer
}
